<script setup lang="ts">
import { watch, computed, ref } from 'vue';
import BadgeClaimType from "@/components/ui/badges/BadgeClaimType.vue";
import splitFlightIdentifier from '../../utils/split-flight-identifier';
import { copyToClipboard } from '@/utils';
import adminAPI from '@/services/api';
import { useNotificationStore, useClaimStore } from '@/store';
import CompanyModal from '../modals/CompanyModal.vue';
import { format } from 'date-fns';

const props = defineProps<{
  flightId: string;
  airlineIconURL?: string;
  airlineId: string;
  airlineCompanyId?: string;
  claimTypeName: any;
  flightIdentifier: string;
  airlineName: string;
  airlineIcao?: string;
  cancelledBy?: string | null;
  cancelledIndicator?: "RECORD" | "SEGMENT";
  cancelledSignature?: string;
  cancelledDate?: Date;
}>();

const icaoFlightIdentifier = computed(() => {
  const { flightNumber} = splitFlightIdentifier(props.flightIdentifier);
  return props.airlineIcao ? props.airlineIcao + flightNumber : props.flightIdentifier;
});

async function monitorFlight(flightId: string) {
  const { success } = await adminAPI.monitorFlight(flightId);
  if (success) {
    useClaimStore().retrieveClaim(useClaimStore().claim.id)
    useNotificationStore().emitNotification({
      title: "Vol synchronisé"
    });
  }
}
</script>
<template>
    <div class="px-2 bg-white">
      <div class="flex py-2">
        <dt class="text-sm font-medium text-gray-500">
          <div v-if="airlineIconURL" class="inline-flex flex-shrink-0 w-14 h-14">
            <img class="rounded-md" :src="airlineIconURL" alt="Airline Logo" />
          </div>
        </dt>
        <dd class="block px-5 mt-1 text-sm text-gray-900">

          <span v-if="cancelledBy" v-tooltip="cancelledBy === 'AIRLINE' ? 'Annulé par la compagnie' : 'Annulé par l\'agence'" class="inline-flex mr-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-red-900 ring-1 ring-inset bg-red-100 ring-red-200">
            <svg class="h-1.5 w-1.5 fill-red-500" viewBox="0 0 6 6" aria-hidden="true">
              <circle cx="3" cy="3" r="3" />
            </svg>
            Segment annulé
          </span>
          <BadgeClaimType class="my-1 mr-2" :name="claimTypeName" :emoji="false" />


          <span v-permission="['UPCLAIM_ADMIN']" class="text-xs font-normal text-gray-500 cursor-pointer">(
              <span @click="copyToClipboard(flightId)">ID du vol</span>
              <span class="px-2">|</span>
              <span @click="monitorFlight(flightId)">sync</span>
            )</span>
          <div class="py-1 whitespace-nowrap">
            Vol
            <span class="font-semibold text-gray-700">
              <a :href="`https://fr.flightaware.com/live/flight/${icaoFlightIdentifier}/history`" target="_blank" class="font-semibold text-gray-700 link">
                {{ flightIdentifier }}
              </a>
            </span>
            opéré par la compagnie
              <div class="inline-flex"><CompanyModal :company-id="props.airlineCompanyId" :relation="{ airlineId }">
                <span class="font-semibold text-gray-700 underline cursor-pointer decoration-indigo-500/30 hover:decoration-indigo-500/50 hover:underline-offset-3 decoration-2 underline-offset-2">{{ airlineName }}</span>
              </CompanyModal>
            </div>
          </div>

          <div class="flex items-center py-1" v-if="cancelledIndicator" v-permission="['UPCLAIM_ADMIN', 'LAWYER']">
              <pre class="px-2 font-medium border border-gray-400 rounded bg-gray-50">{{ cancelledIndicator === 'SEGMENT' ? 'HX' : 'Cancel' }}</pre>
              <span v-if="cancelledSignature || cancelledBy" class="mx-1">par <span class="font-semibold">{{ cancelledSignature || cancelledBy }}</span></span>
              <span v-if="cancelledDate">le {{ format(new Date(cancelledDate), "dd/MM/yyyy à dd'H'mm") }} UTC</span>
          </div>
        </dd>
      </div>
    </div>
</template>